import { useState, useCallback, useEffect } from "react";
import useFetch from "use-http";

import FriendlyTime from "app/components/shared/FriendlyTime";
import Emojify from "app/components/shared/Emojify";
import { StyledUnderline } from "../../shared/styles";
import { FlakyTest, Team } from "../../shared/type";
import TeamAssignmentDropdown, { FormFields } from "../TeamAssignmentDropdown";
import NoTeamsAvailable from "../NoTeamsAvailableDropdown";

type Props = {
  flakyTest: FlakyTest;
  currentAccountMember?: boolean;
  teams: Team[];
  isAdmin?: boolean;
  flakyTestAssignmentFeatureAvailable: boolean;
  organizationSettingsUrl?: string;
  refreshAssignments?: () => Promise<void>;
  isFlaky?: boolean;
};

const FlakyTestRow = ({
  flakyTest,
  currentAccountMember,
  teams,
  isAdmin,
  flakyTestAssignmentFeatureAvailable = false,
  organizationSettingsUrl,
  refreshAssignments,
  isFlaky = true,
}: Props) => {
  const [assignedTeam, setAssignedTeam] = useState<FlakyTest["assigned_team"]>(
    flakyTest.assigned_team,
  );

  const manageAssignments = useFetch({
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": window._csrf.token,
    },
  });

  const handleRemoveAssignment = useCallback(
    async (assignment: FlakyTest["assigned_team"]) => {
      if (!assignment?.id) {
        return false;
      }

      await manageAssignments.delete(
        `${flakyTest.test_assignment_endpoint}/${flakyTest.test_id}?team_id=${assignment.id}`,
      );

      if (manageAssignments.response.ok) {
        if (refreshAssignments) {
          refreshAssignments();
        } else {
          setAssignedTeam(undefined);
        }
      }

      return !!manageAssignments.response.ok;
    },
    [flakyTest],
  );

  const handleCreateAssignment = useCallback(async (data: FormFields) => {
    await manageAssignments.post(
      `${flakyTest.test_assignment_endpoint}?test_uuid=${flakyTest.test_id}&team_id=${data.teamId}`,
    );

    if (manageAssignments.response.ok) {
      if (refreshAssignments) {
        refreshAssignments();
      } else {
        setAssignedTeam(manageAssignments.response.data);
      }
    }

    return !!manageAssignments.response.ok;
  }, []);

  const showAssignmentDropdown = currentAccountMember;

  useEffect(() => {
    setAssignedTeam(flakyTest.assigned_team);
  }, [flakyTest.assigned_team]);

  return (
    <div
      className="ta-suite-card ta-card__container--body"
      style={{ paddingRight: showAssignmentDropdown ? "0" : undefined }}
    >
      <div className="main-section">
        {assignedTeam?.name && flakyTestAssignmentFeatureAvailable && (
          <span className="badge bg-light-purple purple semi-bold">
            <Emojify text={`Assigned to ${assignedTeam.name}`} />
          </span>
        )}
        <a
          href={flakyTest.test_path}
          className="block text-decoration-none color-inherit row-link"
        >
          <span className="semi-bold block">{flakyTest.description}</span>
        </a>
        <span className="mt1 small dark-gray block">{flakyTest.location}</span>
      </div>
      <div className="secondary-section">
        {isFlaky && (
          <div className="pr3 right-align" style={{ width: "6rem" }}>
            {flakyTest.instances}
            <span className="hide"> flaky instances</span>
          </div>
        )}
        <div className="flex justify-between items-center">
          {!isFlaky && (
            <div className="relative z1" style={{ width: "15rem" }}>
              <span className="hide">Assigned at</span>
              <StyledUnderline
                as={FriendlyTime}
                value={assignedTeam?.created_at}
              />
            </div>
          )}
          <div className="relative z1" style={{ width: "15rem" }}>
            <span className="hide">Latest instance at</span>
            <StyledUnderline
              as={FriendlyTime}
              value={flakyTest.most_recent_instance_at}
            />
          </div>
          {showAssignmentDropdown &&
            (teams.length > 0 && flakyTestAssignmentFeatureAvailable ? (
              <TeamAssignmentDropdown
                teams={teams}
                assignment={assignedTeam}
                onCreateAssignment={handleCreateAssignment}
                onRemoveAssignment={handleRemoveAssignment}
              />
            ) : (
              <NoTeamsAvailable
                isAdmin={isAdmin}
                flakyTestAssignmentFeatureAvailable={
                  flakyTestAssignmentFeatureAvailable
                }
                organizationSettingsUrl={organizationSettingsUrl}
              />
            ))}
        </div>
      </div>
    </div>
  );
};

export default FlakyTestRow;
